import { memo, useContext, useState, useEffect, lazy } from 'react';
import { formatNumber, formatCurrency } from '@atc/string-fns';
import { AlertAutoClose } from 'reaxl';
import { useElementObserver } from '@atc/use-element-observer';
import { ratesLabels } from 'axl-config';
import CalculatorInput from './CalculatorInput.js';
import TradeInfoInputs from './TradeInfoInputs.js';
import ConfigContext from '../../ConfigContext/ConfigContext.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
const SelectButtonContainer = /*#__PURE__*/lazy(() => import(/* webpackPrefetch: true */
/* webpackChunkName: "SelectButtonContainer" */
'./SelectButtonContainer.js'));
const TermSelect = /*#__PURE__*/lazy(() => import(/* webpackPrefetch: true */
/* webpackChunkName: "TermSelect" */
'./TermSelect.js'));

/**
 * Lease tab of Native Deal Calculator
 *
 * Renders the lease tab content with calculator inputs for a vehicle lease including
 * lease terms, credit rating, due at signing amount, and optional trade-in information.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array<{label: string, value: string}>} props.creditTierOptions - Array of credit tier options for the dropdown select
 * @param {string} props.dealType - Current deal type, used for conditional alert display
 * @param {number} props.dueAtSigningValue - Amount due at signing value
 * @param {string} props.formattedTotalPrice - Formatted total price of the vehicle
 * @param {Function} props.handleSelectChange - Handler for select input changes
 * @param {Object} props.inputProps - Props to pass to input elements
 * @param {number} props.leaseDownPayment - Down payment amount for the lease
 * @param {Array<{label: string, value: string|number}>} props.leaseMileageOptions - Array of mileage options for the dropdown select
 * @param {Object} props.leaseOffersObj - Object containing lease offer details
 * @param {Array<{label: string, value: string|number}>} props.leaseTermOptions - Array of lease term options for the dropdown select
 * @param {string} props.previousCompletedInput - Name of the most recently completed input field
 * @param {Function} props.renderTaxesAndFees - Function to render taxes and fees section for a given tab
 * @param {string} props.selectedCreditTier - Currently selected credit tier value
 * @param {string|number} props.selectedLeaseTerm - Currently selected lease term value
 * @param {string|number} props.selectedMileage - Currently selected mileage value
 * @param {Object} props.tradeInfoInputProps - Props for the trade-in input fields
 * @returns {JSX.Element} The LeaseTab component
 */
function LeaseTab(_ref) {
  let {
    creditTierOptions,
    dealType,
    dueAtSigningValue,
    formattedTotalPrice,
    handleSelectChange,
    inputProps,
    leaseDownPayment,
    leaseMileageOptions,
    leaseOffersObj,
    leaseTermOptions,
    previousCompletedInput,
    renderTaxesAndFees,
    selectedCreditTier,
    selectedLeaseTerm,
    selectedMileage,
    tradeInfoInputProps
  } = _ref;
  const {
    disableTrade,
    isDueAtSigningAmountDeduct = false,
    oemLabels = {}
  } = useContext(ConfigContext) ?? {};
  const {
    cashDownLabelDS
  } = oemLabels;
  const [showAlert, setShowAlert] = useState(false);
  const [containerElement, containerRef] = useElementObserver();

  // destructure lease object when present
  const {
    dueInAdvance = {},
    downPayment
  } = leaseOffersObj;
  const {
    firstPayment
  } = dueInAdvance;

  // display, show and hide dueAtSigningAlert
  useEffect(() => {
    if (dealType === 'lease' && leaseDownPayment >= 0 && leaseDownPayment < firstPayment) {
      setShowAlert(true);
    }
  }, [leaseDownPayment, firstPayment, dealType]);
  const hideDueAtSigningAlert = () => {
    setShowAlert(false);
  };
  const creditRatingChildren = /*#__PURE__*/jsx(TermSelect, {
    name: "creditTierInput",
    id: "creditTierInput-finance",
    label: "Credit Rating",
    labelClassName: "display-none display-sm-block text-size-300",
    value: selectedCreditTier,
    options: creditTierOptions,
    handleChange: handleSelectChange
  });
  const dueAtSigningChildren = /*#__PURE__*/jsx(CalculatorInput, {
    name: "dueAtSigning",
    label: !isDueAtSigningAmountDeduct ? cashDownLabelDS || 'Cash Down' : 'Due at Signing',
    inputProps: inputProps,
    value: downPayment || dueAtSigningValue,
    hideGlyph: true,
    supportingLink: false,
    tabName: "lease"
  });
  const listingPriceChildren = /*#__PURE__*/jsx(CalculatorInput, {
    name: "listingPrice",
    label: "Listing Price",
    inputProps: {
      ...inputProps,
      className: 'margin-bottom-4'
    },
    value: formattedTotalPrice,
    supportingLink: false,
    tabName: "lease",
    disableInput: true,
    showTextField: true
  }, "listingPrice");
  const termsChildren = /*#__PURE__*/jsxs("div", {
    className: "display-flex flew-row justify-content-between",
    children: [/*#__PURE__*/jsx(TermSelect, {
      label: "Months",
      value: selectedLeaseTerm,
      options: leaseTermOptions,
      name: "leaseTermInput",
      handleChange: handleSelectChange,
      className: "flex-1"
    }), /*#__PURE__*/jsx(TermSelect, {
      label: "Miles Per Year",
      value: selectedMileage,
      options: leaseMileageOptions,
      name: "mileageInput",
      handleChange: handleSelectChange,
      className: "flex-1"
    })]
  });
  const tradeInChildren = !disableTrade && /*#__PURE__*/jsx(TradeInfoInputs, {
    ...tradeInfoInputProps,
    inputProps: inputProps,
    tabName: "lease"
  });
  const calcInputs = [{
    heading: 'Lease Terms',
    primaryLabel: 'Lease Terms',
    children: termsChildren,
    'data-cmp': 'leaseTerms',
    secondaryLabel: `${selectedLeaseTerm} months, ${formatNumber({
      value: selectedMileage
    })} miles`,
    containerElement,
    inputCompletedState: previousCompletedInput === 'leaseTermInput',
    analyticProps: {
      cmp: 'nds_lease-term',
      userModified: 'LEASE_TERM_LENGTH_CHANGED'
    }
  }, {
    heading: 'Credit Rating',
    primaryLabel: 'Credit Rating',
    children: creditRatingChildren,
    secondaryLabel: ratesLabels[selectedCreditTier]?.label,
    containerElement,
    position: 'right',
    inputCompletedState: previousCompletedInput === 'creditTierInput',
    analyticProps: {
      cmp: 'nds_credit-rating',
      userModified: 'CREDIT_VERIFICATION_CLICKED'
    }
  }];
  const tradeInCalcInput = {
    analyticProps: {
      cmp: 'nds_trade-in',
      userModified: 'TRADE_IN_VALUE_CHANGED'
    },
    children: tradeInChildren,
    containerElement,
    heading: 'Trade-In',
    inputCompletedState: previousCompletedInput === 'tradeInInput',
    position: 'right',
    primaryLabel: 'Trade-In',
    secondaryLabel: formatCurrency(tradeInfoInputProps.valueTradeIn - tradeInfoInputProps.valueOwed || 0)
  };

  // Add trade-in to the front of the array if trade is not disabled
  if (!disableTrade) {
    calcInputs.unshift(tradeInCalcInput);
  }
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(AlertAutoClose, {
      bsStyle: "warning",
      show: showAlert,
      onHide: hideDueAtSigningAlert,
      sticky: true,
      timeout: 6000,
      children: "Because of required fees, Amount Due at Signing might not be adjustable. Talk to the dealer for details."
    }), listingPriceChildren, /*#__PURE__*/jsxs("div", {
      className: "row margin-bottom-1",
      ref: containerRef,
      children: [/*#__PURE__*/jsx("div", {
        className: disableTrade ? 'col-xs-12' : 'col-xs-6',
        children: dueAtSigningChildren
      }), /*#__PURE__*/jsx(SelectButtonContainer, {
        calcInputs: calcInputs
      })]
    }), renderTaxesAndFees('lease')]
  });
}
var LeaseTab$1 = /*#__PURE__*/memo(LeaseTab);
export { LeaseTab$1 as default };
